import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import HotelMarketInsightCardsDemandIndicators from 'lumen/components/hotel/market-insight/cards/demand-indicators';
import HotelMarketInsightCardsDemandIndicatorsDetails from 'lumen/components/hotel/market-insight/cards/demand-indicators-details';
import HotelMarketInsightCardsEmptyStateOccupancyCard from 'lumen/components/hotel/market-insight/cards/empty-state-occupancy-card';
import HotelMarketInsightCardsOccupancy from 'lumen/components/hotel/market-insight/cards/occupancy';
import HotelMarketInsightCardsPriceLevelDistribution from 'lumen/components/hotel/market-insight/cards/price-level-distribution';
import HotelMarketInsightCardsPriceLevelDistributionDetails from 'lumen/components/hotel/market-insight/cards/price-level-distribution-details';
import HotelMarketInsightCardsSearches from 'lumen/components/hotel/market-insight/cards/searches';
import HotelMarketInsightCardsSearchesDetails from 'lumen/components/hotel/market-insight/cards/searches-details';
import HotelMarketInsightCardsSmartCompsetDetails from 'lumen/components/hotel/market-insight/cards/smart-compset-details';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import hasFeature from 'lumen/helpers/has-feature';
import type { Levelbucket } from 'lumen/models';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
  MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
  MI_SEARCHES_TYPE_HOTELS_BUSINESS,
  MI_SEARCHES_TYPE_HOTELS_LEISURE,
} from 'lumen/utils/constants/general-constants';
import {
  ACTIVE_FILTERS,
  DEMAND_INDEX_LEVEL_BUCKET_IDS,
  DETAIL_PANE_DETAIL_SECTION_CARD_IDS,
  SHORT_TERM_RENTAL_TYPE_AIRBNB,
} from 'lumen/utils/constants/market-insight-constants';
import { getSummaryForSelectedDayAndExtractDate } from 'lumen/utils/market-insight/market-insight-helpers';

interface HotelMarketInsightDetailPaneSignature {
  Args: {
    clearActiveFilter: unknown;
    currentExtractDate: unknown;
    focusedDetailSection: unknown;
    isDetailLoading: unknown;
    isLoading: unknown;
    isMarketInsightFull: unknown;
    model: unknown;
    parsedActiveFilters: unknown;
    rateIndexDataForCurrentSelection: unknown;
    refDaysAgo: unknown;
    selectedDay: unknown;
    selectedDayModel: unknown;
    setFocusedDetailSection: unknown;
    setHighlightedMarker: unknown;
    updateSearchesDetailsItem: unknown;
  };
}

export default class HotelMarketInsightDetailPane extends Component<HotelMarketInsightDetailPaneSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare miHotels: Services['mi-hotels'];
  @service declare miSearches: Services['mi-searches'];
  @service declare miSelections: Services['mi-selections'];
  @service declare store: Services['store'];

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  @tracked searchesTooltipText = this.miSearches.searchesTooltipText(
    this.flightLeisureSearchSummaryForCurrentSelection,
    this.flightBusinessSearchSummaryForCurrentSelection,
    this.hotelLeisureSearchSummaryForCurrentSelection,
    this.hotelBusinessSearchSummaryForCurrentSelection,
  );

  // Selects the correct altloding summary object (type Airbnb) in the store for the current selected day and extract date
  // Note: Airbnb data is not shown in the overview, as a result, it only arrives from the detail endpoint
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get alternativeLodgingSummaryAirBnbForCurrentSelection() {
    if (!this.args.isDetailLoading && this.args.selectedDayModel) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const selectedDay = this.args.selectedDayModel.date.format('YYYY-MM-DD');
      const currentAlternativeLodgingSummary =
        getSummaryForSelectedDayAndExtractDate(
          this.store,
          'alternativelodgingsummary',
          `${SHORT_TERM_RENTAL_TYPE_AIRBNB},`,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          selectedDay,
          // @ts-expect-error: Incorrect type
          this.args.currentExtractDate,
        );

      return currentAlternativeLodgingSummary;
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get alternativeLodgingSummaryTooltipText() {
    // @ts-expect-error: Incorrect type
    const { numHotelLikeProperties, numProperties } =
      this.alternativeLodgingSummaryAirBnbForCurrentSelection ?? {};

    if (
      Number.isFinite(numHotelLikeProperties) &&
      Number.isFinite(numProperties)
    ) {
      return this.intl.t(
        'market_insight.kpi_explanation.unavailable_alt_lodging.with_numbers',
        {
          hotelLikePercentageNumber: Math.round(
            (numHotelLikeProperties / numProperties) * 100,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          numProperties,
        },
      );
    }

    return this.intl.t(
      'market_insight.kpi_explanation.unavailable_alt_lodging.no_numbers',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get flightBusinessSearchSummaryForCurrentSelection() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.miSearches.getSearchSummaryForCurrentSelection(
      MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
      this.isDetailLoading,
      this.isCalendarLoading,
      this.args.selectedDayModel,
      this.args.currentExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get flightLeisureSearchSummaryForCurrentSelection() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.miSearches.getSearchSummaryForCurrentSelection(
      MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
      this.isDetailLoading,
      this.isCalendarLoading,
      this.args.selectedDayModel,
      this.args.currentExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSearches() {
    return (
      this.miSearches
        ?.areFlightLeisureSearchesSupportedForTheCurrentDestination ||
      this.miSearches
        ?.areFlightBusinessSearchesSupportedForTheCurrentDestination ||
      this.miSearches
        ?.areHotelLeisureSearchesSupportedForTheCurrentDestination ||
      this.miSearches?.areHotelBusinessSearchesSupportedForTheCurrentDestination
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelBusinessSearchSummaryForCurrentSelection() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.miSearches.getSearchSummaryForCurrentSelection(
      MI_SEARCHES_TYPE_HOTELS_BUSINESS,
      this.isDetailLoading,
      this.isCalendarLoading,
      this.args.selectedDayModel,
      this.args.currentExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelLeisureSearchSummaryForCurrentSelection() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.miSearches.getSearchSummaryForCurrentSelection(
      MI_SEARCHES_TYPE_HOTELS_LEISURE,
      this.isDetailLoading,
      this.isCalendarLoading,
      this.args.selectedDayModel,
      this.args.currentExtractDate,
    );
  }

  @computed('args.model.calendar.isLoading')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCalendarLoading() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.model?.calendar?.isLoading;
  }

  @computed('args.model.detail.isLoading')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDetailLoading() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.model?.detail?.isLoading;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoadingSummariesOrDetail() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.isDetailLoading || this.isCalendarLoading;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateIndexesForCurrentSelectionPerSubmarketLevelBucket() {
    const result = {};

    if (!this.args.isLoading && this.args.rateIndexDataForCurrentSelection) {
      const bucketToUse = this.store.peekRecord<Levelbucket>(
        'levelbucket',
        DEMAND_INDEX_LEVEL_BUCKET_IDS.DOW,
      );

      // We need the own hotel rateindex data for the lower/upperbound price calculation for each bucket
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const ownHotelRateIndexData =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.rateIndexDataForCurrentSelection.filter(
          // @ts-expect-error: Incorrect type
          (rateIndexObject) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !rateIndexObject.isMissing &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !rateIndexObject.isRateIndexSoldOut &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !rateIndexObject.isRatesSoldOut &&
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            rateIndexObject.ourHotelId === this.miHotels.ownOurHotelId,
        );

      if (bucketToUse) {
        bucketToUse.levels.forEach((bucket) => {
          // @ts-expect-error: Incorrect type
          result[bucket.level] = {
            rateIndexes: [],
            containsOwnHotel: false,
            bucketLowerBound: bucket.lower_bound,
            bucketUpperBound: bucket.upper_bound,
            bucketLowerBoundPrice: null,
            bucketUpperBoundPrice: null,
            bucketBoundsPriceCurrency: null,
            hotelNames: [],
          };

          // Check if the rateindex data for the own hotel exists (can be missing)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (ownHotelRateIndexData.length) {
            // Note: don't calculate lowerbound price for the first level, and don't calculate upperbound price for the last level
            // These levels don't really have a lower / upperbound. This will lead to the following text being displayed in the tooltip: "You should price lower/higher than ..."
            // It doesn't make sense to display "You should price between X and Y" for lowest & highest level
            if (
              bucket.level !== bucketToUse.levels.length &&
              bucket.upper_bound
            ) {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              result[bucket.level].bucketUpperBoundPrice = Math.round(
                (bucket.upper_bound + 1) *
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  ownHotelRateIndexData[0].usualRateDow,
              );
            }

            if (bucket.level !== 1 && bucket.lower_bound) {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              result[bucket.level].bucketLowerBoundPrice = Math.round(
                (bucket.lower_bound + 1) *
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  ownHotelRateIndexData[0].usualRateDow,
              );
            }

            // The bucket upper & lower boundaries rates we calculate should use the same currency as in the rateindex object of the own hotel
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            result[bucket.level].bucketBoundsPriceCurrency =
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              ownHotelRateIndexData[0].currency;
          }
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.rateIndexDataForCurrentSelection
        // @ts-expect-error: Incorrect type
        .filter(
          // @ts-expect-error: Incorrect type
          (rateIndexObject) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !rateIndexObject.isMissing &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !rateIndexObject.isRateIndexSoldOut &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !rateIndexObject.isRatesSoldOut,
        )
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .forEach((rateIndexObject) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const demandLevel = rateIndexObject.demandLevelColorScaleNumberDow;

          // demandLevel can be null, so add if to be sure
          if (demandLevel) {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            result[demandLevel].rateIndexes.push(rateIndexObject);

            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (rateIndexObject.ourHotelId === this.miHotels.ownOurHotelId) {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              result[demandLevel].containsOwnHotel = true;
            }
          }
        });
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get summaryForCurrentSelection() {
    if (!this.isDetailLoading && !this.isCalendarLoading) {
      // Regular summaries have no type
      const currentSummary = getSummaryForSelectedDayAndExtractDate(
        this.store,
        'summary',
        '',
        // @ts-expect-error: Incorrect type
        this.args.selectedDay,
        this.args.currentExtractDate,
        null,
      );

      if (!currentSummary) {
        return {
          formattedDestinationDemandIndexPercentage: this.intl.t(
            'general.not_available_short',
          ),
          formattedDemandIndexPercentage: this.intl.t(
            'general.not_available_short',
          ),
        };
      }

      return currentSummary;
    }

    if (!this.isCalendarLoading && this.isDetailLoading) {
      // We already have the mostRecentDailySummary property on the day model. If the detail request is loading, we can show this.
      // This prevents the card values from disappearing when switching selected days.
      if (
        this.args.selectedDayModel &&
        // @ts-expect-error: Incorrect type
        this.args.selectedDayModel.mostRecentDailySummary
      ) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.args.selectedDayModel.mostRecentDailySummary;
      }
    }

    return null;
  }

  @action
  onBackButtonClick(): void {
    if (this.args.focusedDetailSection) {
      // @ts-expect-error: Incorrect type
      this.args.setFocusedDetailSection('');
    } else {
      // Reset selected searchDetailsItem
      // @ts-expect-error: Incorrect type
      this.args.updateSearchesDetailsItem(
        DEFAULT_QP_VALUES.hotel['market-insight'].searchesDetailsItem,
      );
      // @ts-expect-error: Incorrect type
      this.args.clearActiveFilter(
        ACTIVE_FILTERS.DAY,
        // @ts-expect-error: Incorrect type
        this.args.parsedActiveFilters[ACTIVE_FILTERS.DAY],
      );
    }
  }

  <template>
    {{! @glint-nocheck }}
    <div class="detail-pane flex flex-col">
      <div class="detail-pane__fixed">
        <div
          class="title-selected-day-container"
          data-test-id="detail-pane-day"
        >
          <h3 class="text-neutral-emphasis">
            {{dayjsFormat
              @selectedDay
              format=this.miSelections.defaultDateFormatWithDOW
            }}
          </h3>
          <button
            class="back-button button--unstyled flex flex-row items-center"
            data-test-id="detail-pane-day-back-button"
            type="button"
            {{on "click" this.onBackButtonClick}}
          >
            <PrismIcon @name="arrow-left" @size="small" />
            {{#if @focusedDetailSection}}
              <p class="text-200--regular ml-100">
                {{t "market_insight.back_to_day_summary"}}
              </p>
            {{else}}
              <p class="text-200--regular ml-100">
                {{t "market_insight.back_to_home"}}
              </p>
            {{/if}}
          </button>
        </div>
      </div>
      {{#if @focusedDetailSection}}
        {{#if
          (eq
            @focusedDetailSection
            DETAIL_PANE_DETAIL_SECTION_CARD_IDS.DEMAND_INDICATORS
          )
        }}
          <HotelMarketInsightCardsDemandIndicatorsDetails
            @alternativeLodgingSummary={{this.alternativeLodgingSummaryAirBnbForCurrentSelection}}
            @alternativeLodgingSummaryTooltipText={{this.alternativeLodgingSummaryTooltipText}}
            @refDaysAgo={{@refDaysAgo}}
            @selectedDayModel={{@selectedDayModel}}
            @setHighlightedMarker={{@setHighlightedMarker}}
            @summaryForCurrentSelection={{this.summaryForCurrentSelection}}
          />
        {{else if
          (eq
            @focusedDetailSection
            DETAIL_PANE_DETAIL_SECTION_CARD_IDS.PRICE_LEVEL_DISTRIBUTION
          )
        }}
          <HotelMarketInsightCardsPriceLevelDistributionDetails
            @isLoadingSummariesOrDetail={{this.isLoadingSummariesOrDetail}}
            @rateIndexDataForCurrentSelection={{@rateIndexDataForCurrentSelection}}
            @rateIndexesForCurrentSelectionPerSubmarketLevelBucket={{this.rateIndexesForCurrentSelectionPerSubmarketLevelBucket}}
            @selectedDayModel={{@selectedDayModel}}
            @setHighlightedMarker={{@setHighlightedMarker}}
          />
        {{/if}}
        {{#if
          (eq
            @focusedDetailSection DETAIL_PANE_DETAIL_SECTION_CARD_IDS.SEARCHES
          )
        }}
          <HotelMarketInsightCardsSearchesDetails
            @currentExtractDate={{@currentExtractDate}}
            @isCalendarLoading={{this.isCalendarLoading}}
            @isDetailLoading={{this.isDetailLoading}}
            @searchesTooltipText={{this.searchesTooltipText}}
            @selectedDayModel={{@selectedDayModel}}
            @updateSearchesDetailsItem={{@updateSearchesDetailsItem}}
          />
        {{/if}}
        {{#if
          (eq
            @focusedDetailSection
            DETAIL_PANE_DETAIL_SECTION_CARD_IDS.COMPETITORS_LIST
          )
        }}
          <HotelMarketInsightCardsSmartCompsetDetails
            @isDetailLoading={{this.isDetailLoading}}
            @isLoadingSummariesOrDetail={{this.isLoadingSummariesOrDetail}}
            @rateIndexDataForCurrentSelection={{@rateIndexDataForCurrentSelection}}
            @selectedDayModel={{@selectedDayModel}}
            @setHighlightedMarker={{@setHighlightedMarker}}
          />
        {{/if}}
      {{else}}
        <HotelMarketInsightCardsDemandIndicators
          @alternativeLodgingSummary={{this.alternativeLodgingSummaryAirBnbForCurrentSelection}}
          @alternativeLodgingSummaryTooltipText={{this.alternativeLodgingSummaryTooltipText}}
          @detailSectionId={{DETAIL_PANE_DETAIL_SECTION_CARD_IDS.DEMAND_INDICATORS}}
          @isLoading={{this.isLoadingSummariesOrDetail}}
          @refDaysAgo={{@refDaysAgo}}
          @selectedDayModel={{@selectedDayModel}}
          @setFocusedDetailSection={{@setFocusedDetailSection}}
          @summaryForCurrentSelection={{this.summaryForCurrentSelection}}
        />
        {{#if this.access.hasOTBView}}
          {{#if
            (or
              (hasFeature "otb.empty_placeholder")
              (hasFeature "otb.data_validation")
            )
          }}
            <HotelMarketInsightCardsEmptyStateOccupancyCard />
          {{else}}
            <HotelMarketInsightCardsOccupancy
              @currentExtractDate={{@currentExtractDate}}
              @refDaysAgo={{@refDaysAgo}}
              @selectedDayModel={{@selectedDayModel}}
            />
          {{/if}}
        {{/if}}
        {{#if @isMarketInsightFull}}
          <HotelMarketInsightCardsPriceLevelDistribution
            @currentExtractDate={{@currentExtractDate}}
            @detailSectionId={{DETAIL_PANE_DETAIL_SECTION_CARD_IDS.PRICE_LEVEL_DISTRIBUTION}}
            @isLoadingSummariesOrDetail={{this.isLoadingSummariesOrDetail}}
            @rateIndexDataForCurrentSelection={{@rateIndexDataForCurrentSelection}}
            @rateIndexesForCurrentSelectionPerSubmarketLevelBucket={{this.rateIndexesForCurrentSelectionPerSubmarketLevelBucket}}
            @selectedDayModel={{@selectedDayModel}}
            @setFocusedDetailSection={{@setFocusedDetailSection}}
          />
        {{/if}}
        {{#if this.hasSearches}}
          <HotelMarketInsightCardsSearches
            @detailSectionId={{DETAIL_PANE_DETAIL_SECTION_CARD_IDS.SEARCHES}}
            @flightBusinessSearchSummaryForCurrentSelection={{this.flightBusinessSearchSummaryForCurrentSelection}}
            @flightLeisureSearchSummaryForCurrentSelection={{this.flightLeisureSearchSummaryForCurrentSelection}}
            @hotelBusinessSearchSummaryForCurrentSelection={{this.hotelBusinessSearchSummaryForCurrentSelection}}
            @hotelLeisureSearchSummaryForCurrentSelection={{this.hotelLeisureSearchSummaryForCurrentSelection}}
            @searchesTooltipText={{this.searchesTooltipText}}
            @selectedDayModel={{@selectedDayModel}}
            @setFocusedDetailSection={{@setFocusedDetailSection}}
          />
        {{/if}}
      {{/if}}
    </div>
  </template>
}
